import {
  BugIcon,
  PlusIcon,
  RocketLaunch,
  Kanban,
  ListBullets,
} from "@phosphor-icons/react";
import { useState, useEffect, useMemo, useRef } from "react";
import { useSession, useUser } from "@clerk/clerk-react";
import { useNavigate, useLocation } from "react-router-dom";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { BugReportKanbanView } from "./components/BugReportKanbanView";
import { BugReportListView } from "./components/BugReportListView";
import { AddBugReportModal } from "./components/AddBugReportModal";
import { BugReportCardSkeleton } from "./components/BugReportCardSkeleton";
import { BugReport, BugStatus, ItemType } from "shared/types/bug-report";
import { FilterBar } from "shared/components/filterBar";
import { FilterChip, FilterSection } from "shared/components/filterBar/types";
import { Button } from "@/components/ui/button";
import { EpicsOverviewPage } from "./epics/epics-overview";
import { useUsers } from "shared/contexts/users.context";
import { PREDEFINED_BUG_REPORT_TAGS } from "./bugReportTags";

type ViewMode = "kanban" | "list";
const VIEW_MODE_STORAGE_KEY = "bugReportsViewMode";
const FILTERS_STORAGE_KEY = "bugReportsFilters.v1";

interface PersistedFilters {
  searchValue: string;
  selectedStatuses: string[];
  selectedTags: string[];
  excludedTags: string[];
  selectedItemType: 'all' | 'bugs' | 'features';
  selectedReporters: string[];
  selectedAssignees: string[];
}

const readPersistedFilters = (): Partial<PersistedFilters> => {
  if (typeof window === "undefined") return {};
  try {
    const raw = sessionStorage.getItem(FILTERS_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return {};
    return parsed as Partial<PersistedFilters>;
  } catch (error) {
    console.error("Failed to read persisted bug report filters", error);
    return {};
  }
};

export const BugReportsPage = () => {
  const { session } = useSession();
  const { user: currentUser } = useUser();
  const { users } = useUsers();
  const navigate = useNavigate();
  const location = useLocation();
  const [bugs, setBugs] = useState<BugReport[]>([]);
  const [loading, setLoading] = useState(true);
  const hasFetchedRef = useRef(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    if (typeof window === "undefined") return "kanban";
    const stored = localStorage.getItem(VIEW_MODE_STORAGE_KEY);
    return stored === "list" || stored === "kanban" ? stored : "kanban";
  });

  const handleViewModeChange = (mode: ViewMode) => {
    setViewMode(mode);
    if (typeof window !== "undefined") {
      localStorage.setItem(VIEW_MODE_STORAGE_KEY, mode);
    }
  };

  // Determine which view to show based on URL
  const isEpicsView = location.pathname === '/bug-reports/epics' || location.pathname.includes('/epics/');

  // Filter states (restored from sessionStorage so they survive navigation to detail pages)
  const persistedFilters = useRef(readPersistedFilters()).current;
  const [searchValue, setSearchValue] = useState<string>(persistedFilters.searchValue ?? '');
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>(persistedFilters.selectedStatuses ?? []);
  const [selectedTags, setSelectedTags] = useState<string[]>(persistedFilters.selectedTags ?? []);
  const [excludedTags, setExcludedTags] = useState<string[]>(persistedFilters.excludedTags ?? []);
  const [selectedItemType, setSelectedItemType] = useState<'all' | 'bugs' | 'features'>(persistedFilters.selectedItemType ?? 'all');
  const [selectedReporters, setSelectedReporters] = useState<string[]>(persistedFilters.selectedReporters ?? []);
  const [selectedAssignees, setSelectedAssignees] = useState<string[]>(persistedFilters.selectedAssignees ?? []);

  // Persist filters to sessionStorage whenever they change
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const toPersist: PersistedFilters = {
        searchValue,
        selectedStatuses,
        selectedTags,
        excludedTags,
        selectedItemType,
        selectedReporters,
        selectedAssignees,
      };
      sessionStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(toPersist));
    } catch (error) {
      console.error("Failed to persist bug report filters", error);
    }
  }, [searchValue, selectedStatuses, selectedTags, excludedTags, selectedItemType, selectedReporters, selectedAssignees]);

  const PREDEFINED_TAGS = PREDEFINED_BUG_REPORT_TAGS;

  const fetchBugs = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/bug-reports`,
        { method: "GET" },
        session
      );
      if (response.ok) {
        const data = await response.json();
        setBugs(data);
      } else {
        console.error("Failed to fetch bugs");
      }
    } catch (error) {
      console.error("Error fetching bugs:", error);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    // Fetch once when session becomes available, avoid refetch on window focus/session refresh
    if (!session || hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    fetchBugs(false);
  }, [session]);

  const handleBugClick = (bug: BugReport) => {
    navigate(`/bug-reports/${bug.id}`);
  };

  // Calculate tag counts from all bugs
  const tagCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    bugs.forEach(bug => {
      bug.tags?.forEach(tag => {
        counts[tag] = (counts[tag] || 0) + 1;
      });
    });
    return counts;
  }, [bugs]);

  // Get all unique tags (predefined + custom)
  const allTags = useMemo(() => {
    const uniqueTags = new Set([...PREDEFINED_TAGS]);
    bugs.forEach(bug => {
      bug.tags?.forEach(tag => uniqueTags.add(tag));
    });
    return Array.from(uniqueTags).sort();
  }, [bugs, PREDEFINED_TAGS]);

  // Helper to get user name
  const getUserName = (userId: string) => {
    const user = users.find(u => u.id === userId);
    if (user) {
      const firstName = user.firstName || user.first_name || "";
      const lastName = user.lastName || user.last_name || "";
      return `${firstName} ${lastName}`.trim() || "Onbekende gebruiker";
    }
    return "Onbekende gebruiker";
  };

  // Filter chips for active filters
  const filterChips: FilterChip[] = useMemo(() => {
    const chips: FilterChip[] = [];

    selectedStatuses.forEach(status => {
      chips.push({
        key: `status-${status}`,
        label: status,
        category: 'Status',
        color: 'moss',
        onRemove: () => setSelectedStatuses(prev => prev.filter(s => s !== status))
      });
    });

    selectedTags.forEach(tag => {
      chips.push({
        key: `tag-${tag}`,
        label: `${tag} (${tagCounts[tag] || 0})`,
        category: 'Module',
        color: 'moss',
        onRemove: () => setSelectedTags(prev => prev.filter(t => t !== tag))
      });
    });

    excludedTags.forEach(tag => {
      chips.push({
        key: `xtag-${tag}`,
        label: `${tag}`,
        category: 'Module uit',
        color: 'brick',
        onRemove: () => setExcludedTags(prev => prev.filter(t => t !== tag))
      });
    });

    selectedReporters.forEach(userId => {
      chips.push({
        key: `reporter-${userId}`,
        label: getUserName(userId),
        category: 'Gemeld door',
        color: 'brick',
        onRemove: () => setSelectedReporters(prev => prev.filter(id => id !== userId))
      });
    });

    selectedAssignees.forEach(userId => {
      chips.push({
        key: `assignee-${userId}`,
        label: getUserName(userId),
        category: 'Toegewezen aan',
        color: 'moss',
        onRemove: () => setSelectedAssignees(prev => prev.filter(id => id !== userId))
      });
    });

    return chips;
  }, [selectedStatuses, selectedTags, excludedTags, selectedReporters, selectedAssignees, tagCounts, users]);

  // Filter sections for the filter panel
  const filterSections: FilterSection[] = useMemo(() => {
    // Build user options with special handling for current user
    const userOptions = users.map(user => {
      const firstName = user.firstName || user.first_name || "";
      const lastName = user.lastName || user.last_name || "";
      const fullName = `${firstName} ${lastName}`.trim() || "Onbekende gebruiker";

      return {
        key: user.id,
        label: user.id === currentUser?.id ? `${fullName} (Ik)` : fullName
      };
    }).sort((a, b) => {
      // Sort with "current user" at the top
      if (a.key === currentUser?.id) return -1;
      if (b.key === currentUser?.id) return 1;
      return a.label.localeCompare(b.label);
    });

    return [
      {
        key: 'reporters',
        label: 'Gemeld door',
        options: userOptions,
        selectedValues: selectedReporters,
        onUpdate: setSelectedReporters,
        color: 'brick',
      },
      {
        key: 'assignees',
        label: 'Toegewezen aan',
        options: userOptions,
        selectedValues: selectedAssignees,
        onUpdate: setSelectedAssignees,
        color: 'moss',
      },
      {
        key: 'tags',
        label: 'Module/Tag (alleen tonen)',
        options: allTags.map(tag => ({
          key: tag,
          label: `${tag} (${tagCounts[tag] || 0})`
        })),
        selectedValues: selectedTags,
        onUpdate: (next: string[]) => {
          setSelectedTags(next);
          // Including a tag clears it from the exclusion list to avoid contradictions.
          if (next.length > 0) {
            setExcludedTags(prev => prev.filter(t => !next.includes(t)));
          }
        },
        color: 'moss',
      },
      {
        key: 'excludedTags',
        label: 'Module/Tag (uitsluiten)',
        options: allTags.map(tag => ({
          key: tag,
          label: `${tag} (${tagCounts[tag] || 0})`
        })),
        selectedValues: excludedTags,
        onUpdate: (next: string[]) => {
          setExcludedTags(next);
          // Excluding a tag clears it from the include list to avoid contradictions.
          if (next.length > 0) {
            setSelectedTags(prev => prev.filter(t => !next.includes(t)));
          }
        },
        color: 'brick',
      },
      {
        key: 'status',
        label: 'Status',
        options: [
          { key: BugStatus.REPORTED, label: 'Gemeld' },
          { key: BugStatus.BACKLOG, label: 'Backlog' },
          { key: BugStatus.IN_PROGRESS, label: 'In behandeling' },
          { key: BugStatus.TESTING, label: 'Testing' },
          { key: BugStatus.RESOLVED, label: 'Opgelost' },
          { key: BugStatus.CLOSED, label: 'Gesloten' },
          { key: BugStatus.PARKED, label: 'Geparkeerd' },
        ],
        selectedValues: selectedStatuses,
        onUpdate: setSelectedStatuses,
        color: 'moss',
      }
    ];
  }, [selectedStatuses, selectedTags, excludedTags, selectedReporters, selectedAssignees, allTags, tagCounts, users, currentUser]);

  // Apply filters to bugs
  const filteredBugs = useMemo(() => {
    let filtered = [...bugs];

    // Item type filter
    if (selectedItemType === 'bugs') {
      filtered = filtered.filter(bug => bug.type === ItemType.BUG);
    } else if (selectedItemType === 'features') {
      filtered = filtered.filter(bug => bug.type === ItemType.FEATURE);
    }

    // Search filter
    if (searchValue.trim()) {
      const query = searchValue.toLowerCase();
      filtered = filtered.filter(bug =>
        bug.title.toLowerCase().includes(query) ||
        bug.description?.toLowerCase().includes(query) ||
        bug.tags?.some(tag => tag.toLowerCase().includes(query))
      );
    }

    // Reporter filter
    if (selectedReporters.length > 0) {
      filtered = filtered.filter(bug => selectedReporters.includes(bug.reportedBy));
    }

    // Assignee filter
    if (selectedAssignees.length > 0) {
      filtered = filtered.filter(bug => bug.assignedTo && selectedAssignees.includes(bug.assignedTo));
    }

    // Tag filter — include
    if (selectedTags.length > 0) {
      filtered = filtered.filter(bug =>
        bug.tags?.some(tag => selectedTags.includes(tag))
      );
    }

    // Tag filter — exclude (hides any bug carrying one of the excluded tags)
    if (excludedTags.length > 0) {
      filtered = filtered.filter(
        bug => !bug.tags?.some(tag => excludedTags.includes(tag))
      );
    }

    // Status filter
    if (selectedStatuses.length > 0) {
      filtered = filtered.filter(bug => selectedStatuses.includes(bug.status));
    } else if (viewMode === 'list') {
      // In list view, hide "Gesloten" and "Opgelost" items by default
      // (unless user explicitly filters on those statuses)
      filtered = filtered.filter(
        bug => bug.status !== BugStatus.CLOSED && bug.status !== BugStatus.RESOLVED,
      );
    }

    return filtered;
  }, [bugs, searchValue, selectedStatuses, selectedTags, excludedTags, selectedReporters, selectedAssignees, selectedItemType, viewMode]);


  return (
    <div className="flex flex-col gap-4 py-6">
      {/* Header */}
      <header className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-brick-primary/10 rounded-full flex items-center justify-center">
            {isEpicsView ? (
              <RocketLaunch weight="duotone" size={28} className="text-moss-primary" />
            ) : (
              <BugIcon weight="duotone" size={28} className="text-brick-primary" />
            )}
          </div>
          <div>
            <h1 className="text-2xl font-semibold">
              {isEpicsView ? 'Roadmap' : 'Bugs & Suggesties'}
            </h1>
            <p className="text-sm text-neutral-600">
              {isEpicsView
                ? 'Beheer grote projecten en initiatieven'
                : `${filteredBugs.length} van ${bugs.length} item${bugs.length !== 1 ? 's' : ''}`
              }
            </p>
          </div>
        </div>
        {!isEpicsView && (
          <div className="flex items-center gap-2">
            {/* View mode toggle (List / Kanban) */}
            <div
              role="group"
              aria-label="Weergave kiezen"
              className="inline-flex overflow-hidden rounded-md border border-neutral-200 bg-white"
            >
              <button
                type="button"
                onClick={() => handleViewModeChange("list")}
                title="Lijst weergave"
                aria-label="Lijst weergave"
                aria-pressed={viewMode === "list"}
                className={`flex h-8 w-9 items-center justify-center border-r border-neutral-200 transition-colors ${
                  viewMode === "list"
                    ? "text-moss-primary bg-neutral-100 ring-moss-primary/40 ring-1 ring-inset"
                    : "text-neutral-secondary-mid hover:bg-neutral-50"
                }`}
              >
                <ListBullets size={16} weight={viewMode === "list" ? "bold" : "regular"} />
              </button>
              <button
                type="button"
                onClick={() => handleViewModeChange("kanban")}
                title="Kanban weergave"
                aria-label="Kanban weergave"
                aria-pressed={viewMode === "kanban"}
                className={`flex h-8 w-9 items-center justify-center transition-colors ${
                  viewMode === "kanban"
                    ? "text-moss-primary bg-neutral-100 ring-moss-primary/40 ring-1 ring-inset"
                    : "text-neutral-secondary-mid hover:bg-neutral-50"
                }`}
              >
                <Kanban size={16} weight={viewMode === "kanban" ? "fill" : "regular"} />
              </button>
            </div>
            <Button size="sm" onClick={() => setIsAddModalOpen(true)}>
              <PlusIcon size={16} />
              Nieuw Item
            </Button>
          </div>
        )}
      </header>

      {/* Content - conditionally render based on URL */}
      {isEpicsView ? (
        <EpicsOverviewPage />
      ) : (
        <div className="space-y-4 mt-6">
          {/* Toggle Buttons for Item Type Filter */}
          <div className="flex items-center gap-2 bg-white rounded-lg border border-neutral-200 p-2 w-fit">
            <Button
              size="sm"
              variant={selectedItemType === 'all' ? 'default' : 'ghost'}
              onClick={() => setSelectedItemType('all')}
              className={selectedItemType === 'all' ? 'bg-moss-primary hover:bg-moss-primary/90' : ''}
            >
              Alle items
            </Button>
            <Button
              size="sm"
              variant={selectedItemType === 'bugs' ? 'default' : 'ghost'}
              onClick={() => setSelectedItemType('bugs')}
              className={selectedItemType === 'bugs' ? 'bg-brick-primary hover:bg-brick-primary/90' : ''}
            >
              🐛 Bugs
            </Button>
            <Button
              size="sm"
              variant={selectedItemType === 'features' ? 'default' : 'ghost'}
              onClick={() => setSelectedItemType('features')}
              className={selectedItemType === 'features' ? 'bg-blue-600 hover:bg-blue-700' : ''}
            >
              ✨ Features
            </Button>
          </div>

          {/* Kanban or List Board */}
          {loading ? (
            <div className="rounded-lg border border-neutral-200 overflow-hidden">
              <div className="p-4">
                <div className="flex gap-3 overflow-x-auto pb-4">
                  {[1, 2, 3, 4, 5].map((col) => (
                    <div key={col} className="flex flex-col gap-2 h-full min-w-[280px]">
                      <div className="rounded-full bg-neutral-100 px-4 py-2.5 shrink-0 animate-pulse">
                        <div className="h-4 bg-neutral-200 rounded w-24"></div>
                      </div>
                      <div className="flex-1 rounded-lg border-2 border-dashed border-neutral-200 bg-neutral-50/30 p-2 min-h-[400px]">
                        <div className="space-y-3">
                          {[1, 2].map((card) => (
                            <BugReportCardSkeleton key={card} />
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : viewMode === "kanban" ? (
            <BugReportKanbanView
              bugs={filteredBugs}
              onUpdate={() => fetchBugs(true)}
              onBugClick={handleBugClick}
            />
          ) : (
            <BugReportListView
              bugs={filteredBugs}
              onBugClick={handleBugClick}
              onUpdate={() => fetchBugs(true)}
            />
          )}

          {/* Filter Bar */}
          <FilterBar
            searchValue={searchValue}
            onSearchChange={setSearchValue}
            searchPlaceholder="Zoek bugs op titel, beschrijving, tags..."
            filterChips={filterChips}
            filterSections={filterSections}
            showWhenEmpty={true}
          />
        </div>
      )}

      {/* Add Bug/Feature Modal */}
      <AddBugReportModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSuccess={() => fetchBugs(true)}
      />
    </div>
  );
};
